import {GoogleMap, useJsApiLoader, Marker, InfoWindow, Polyline} from '@react-google-maps/api';
import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import {User, Location, Session} from "../types/Types";
import {collection, doc, getDoc, getDocs, orderBy, query, where} from "firebase/firestore";
import {db} from "../../firebase/FirebaseProvider";
import {Link, useParams} from "react-router-dom";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faLayerGroup, faSatelliteDish} from "@fortawesome/free-solid-svg-icons";
import {logDataLoaded} from "../util/Util";
import PageTracking from "../auth/PageTracking";
import AuthCheck from '../auth/AuthCheck';
import {usePreventMobileZoom} from '../../hooks/usePreventMobileZoom';
import {GOOGLE_MAPS_API_KEY} from '../../config/googleMaps';

const center = {
    lat: 54.8,
    lng: -3.0
};

interface Props {
    locations: Location[];
    currentUser: User | undefined;
    profileUser?: User;
    inline?: boolean;
}

interface JourneyStop {
    location: Location;
    order: number;
}

const createNumberedMarker = (number: number): string => {
    const fontSize = number < 10 ? 13 : number < 100 ? 11 : 9;
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><circle cx="16" cy="16" r="14" fill="#000000" stroke="white" stroke-width="2"/><text x="16" y="21" text-anchor="middle" fill="white" font-size="${fontSize}" font-weight="bold" font-family="Arial">${number}</text></svg>`;
    return `data:image/svg+xml;base64,${btoa(svg)}`;
};

const polylineOptions = {
    strokeColor: "#000000",
    strokeOpacity: 1,
    strokeWeight: 3,
};

const Journey: React.FC<Props> = (props) => {
    const { id } = useParams<{ id: string }>();
    const [journeyStops, setJourneyStops] = useState<JourneyStop[]>([]);
    const [selectedIndex, setSelectedIndex] = useState<number | undefined>(undefined);
    const [isInfoOpen, setIsInfoOpen] = useState(false);
    const [isSatellite, setIsSatellite] = useState(false);
    const loadedUserIdRef = useRef<string | null>(null);

    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: GOOGLE_MAPS_API_KEY
    });

    const onLoad = useCallback((_map: any) => {}, []);
    const onUnmount = useCallback(() => {}, []);

    const loadJourney = useCallback(async (user: User) => {
        try {
            const sessionsQuery = query(
                collection(db, 'sessions'),
                where('userId', '==', user.id),
                orderBy('checkInTime', 'asc')
            );
            const snapshot = await getDocs(sessionsQuery);
            const sessions: Session[] = [];
            snapshot.forEach((doc) => sessions.push(doc.data() as Session));
            logDataLoaded('journey sessions', sessions.length);

            const locationById = new Map<string, Location>();
            props.locations.forEach((loc) => locationById.set(loc.id, loc));

            // Sessions are already asc — keep first occurrence of each location
            const stops: JourneyStop[] = [];
            const seenLocations = new Set<string>();
            sessions.forEach((session) => {
                if (seenLocations.has(session.locationId)) return;
                seenLocations.add(session.locationId);
                const location = locationById.get(session.locationId);
                if (location && location.lat && location.lng) {
                    stops.push({ location, order: stops.length + 1 });
                }
            });

            setJourneyStops(stops);
        } catch (error) {
            console.log(error);
        }
    }, [props.locations]);

    useEffect(() => {
        if (props.profileUser) {
            if (props.profileUser.id !== loadedUserIdRef.current) {
                loadedUserIdRef.current = props.profileUser.id;
                loadJourney(props.profileUser);
            }
            return;
        }

        if (!id) {
            // no URL param — use current logged-in user
            if (props.currentUser && props.currentUser.id !== loadedUserIdRef.current) {
                loadedUserIdRef.current = props.currentUser.id;
                loadJourney(props.currentUser);
            }
            return;
        }
        if (id === loadedUserIdRef.current) return;
        loadedUserIdRef.current = id;
        // load the profile user by id
        (async () => {
            const userSnap = await getDoc(doc(db, 'users', id));
            if (userSnap.exists()) {
                const user = { ...userSnap.data(), id: userSnap.id } as User;
                loadJourney(user);
            }
        })();
    }, [id, props.currentUser, props.profileUser, loadJourney]);

    const polylinePath = useMemo(() =>
        journeyStops.map((stop) => ({
            lat: +stop.location.lat,
            lng: +stop.location.lng,
        })),
    [journeyStops]);

    // Prevent mobile zoom gestures on this page
    usePreventMobileZoom(!props.inline);

    if (!isLoaded) return null;

    const journeyMap = props.locations && props.locations.length > 0 ? (
        <div className={props.inline ? "user-v2-journey-inline-map" : undefined} style={props.inline ? undefined : {width: "100%", height: "80%"}}>
            <GoogleMap
                mapContainerStyle={{width: "100%", height: props.inline ? "min(55vh, 22rem)" : "67vh"}}
                center={center}
                zoom={props.inline ? 4.9 : 5.4}
                onLoad={onLoad}
                onUnmount={onUnmount}
                options={{
                    mapTypeControl: !props.inline,
                    mapTypeId: isSatellite ? "satellite" : "roadmap",
                    streetViewControl: false,
                }}
            >
                {journeyStops.length > 1 && (
                    <Polyline path={polylinePath} options={polylineOptions} />
                )}
                {journeyStops.map((stop, index) => (
                    <Marker
                        key={stop.location.id}
                        icon={{
                            url: createNumberedMarker(stop.order),
                            scaledSize: new window.google.maps.Size(32, 32),
                            anchor: new window.google.maps.Point(16, 16),
                        }}
                        position={{ lat: +stop.location.lat, lng: +stop.location.lng }}
                        zIndex={stop.order}
                        onClick={() => { setSelectedIndex(index); setIsInfoOpen(true); }}
                        title={stop.location.name}
                    >
                        {isInfoOpen && selectedIndex === index && (
                            <InfoWindow onCloseClick={() => setIsInfoOpen(false)}>
                                <div className="info-window">
                                    <div className="info-header">
                                        <Link to={`/location/${stop.location.id}/comments`}>{stop.location.name}</Link>
                                    </div>
                                    <div className="info-address">
                                        {stop.location.address}
                                    </div>
                                    <div className="mt-1">
                                        Unique check-in #{stop.order}
                                    </div>
                                </div>
                            </InfoWindow>
                        )}
                    </Marker>
                ))}
            </GoogleMap>
            {props.inline && (
                <button
                    aria-label={isSatellite ? "Show map view" : "Show satellite view"}
                    aria-pressed={isSatellite}
                    className={`cursor-pointer v2-map-type-button${isSatellite ? " active" : ""}`}
                    onClick={() => setIsSatellite((satellite) => !satellite)}
                    title={isSatellite ? "Show map view" : "Show satellite view"}
                    type="button"
                >
                    <FontAwesomeIcon icon={isSatellite ? faLayerGroup : faSatelliteDish} />
                </button>
            )}
        </div>
    ) : null;

    if (props.inline) {
        return journeyMap;
    }

    return (
        <>
            <PageTracking />
            <AuthCheck />
            {journeyMap}
        </>
    );
};

export default React.memo(Journey);
